/**
 * Cooperative membership (web) — a farmer's self-service coop mobility surface.
 *
 * Web port of apps/mobile/app/coop-membership.tsx. Scopes to the signed-in user:
 * every endpoint under /me/cooperative is guarded server-side and resolves to the
 * bearer token's `sub`, so there is no user-id param. We still read the JWT `sub`
 * from the access-token claims to guard against an identity-less session (same
 * idiom as _app.my-contributions.tsx) before firing queries.
 *
 * Farmer-initiated side only (coop-admin approve/invite is a separate lane). The
 * single GET /me/cooperative drives a three-way state machine:
 *   - MEMBER (cooperativeId !== null) → status card + "Leave cooperative" (confirm
 *     dialog) → POST /me/cooperative/leave. Invalidates the shared
 *     ['me-farm-profile'] key so the home-screen membership chip updates too.
 *   - PENDING (cooperativeId === null && pendingRequest !== null) → pending state +
 *     requestedAt + "Cancel request" → POST /me/cooperative/cancel-request.
 *   - INDEPENDENT (cooperativeId === null && pendingRequest === null) → intro note
 *     + a second ['joinable-cooperatives'] query (enabled only here) listing each
 *     joinable coop with its own "Request to join" button → confirm dialog →
 *     POST /me/cooperative/join → flips to the pending state.
 *
 * Unlike mobile, web is ONLINE-ONLY — plain react-query online flows, no SQLite
 * cache and no offline banners.
 *
 * Endpoints: GET /me/cooperative, GET /me/cooperatives/joinable,
 * POST /me/cooperative/join, POST /me/cooperative/leave,
 * POST /me/cooperative/cancel-request. Contract from the generated orval client
 * src/api/generated/me/me.ts (TD-070).
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItem,
  Stack,
  Text,
} from '@fse/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError, readAccessTokenClaims } from '../api/client';
import {
  meControllerCancelCooperativeRequest,
  meControllerGetCooperative,
  meControllerJoinCooperative,
  meControllerLeaveCooperative,
  meControllerListJoinableCooperatives,
} from '../api/generated/me/me';
import type { JoinableCoopDto } from '../api/generated/models';

/** Pull an FseApiError's message, else stringify — mirrors my-contributions. */
function errMessage(error: unknown): string {
  return error instanceof FseApiError ? error.message : String(error);
}

/** Format an ISO-8601 timestamp to a readable local date-time, guarding NaN. */
function formatRequestedAt(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString();
}

function Header({ t }: { t: TFunction }) {
  return (
    <Box>
      <Text variant="headline-small" as="h2">
        {t('coop.title')}
      </Text>
      <Box sx={{ mt: 0.5 }}>
        <Text variant="body-medium" color="text.secondary">
          {t('coop.subtitle')}
        </Text>
      </Box>
    </Box>
  );
}

export function CooperativeScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const sub = readAccessTokenClaims()?.sub;

  // Dialog state: leave-confirm flag, and the coop the user is about to request.
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [joinTarget, setJoinTarget] = useState<JoinableCoopDto | null>(null);

  const coopQuery = useQuery({
    queryKey: ['me-cooperative'],
    queryFn: ({ signal }) => meControllerGetCooperative(signal),
    enabled: !!sub,
  });

  const coop = coopQuery.data;
  // TD-057 — membership reflects the persona discriminator, NOT a stale
  // cooperativeId. A group member (principal_type=informal_group) has their
  // group stored as a cooperative row, so cooperativeId is non-null for them
  // too; gating "member" on that would wrongly show them the coop Leave CTA and
  // let them wipe their group. Group, coop and independent are mutually
  // exclusive, so branch on principalType:
  //   - formal_coop    → real coop member (status + Leave).
  //   - informal_group → in a group, NOT a coop: neutral note, no coop CTAs.
  //   - individual     → independent: pending state or the join picker.
  const isGroupMember = coop != null && coop.principalType === 'informal_group';
  const isMember = coop != null && coop.principalType === 'formal_coop';
  const hasPending =
    coop != null && coop.principalType === 'individual' && coop.pendingRequest !== null;
  // Only an independent (individual) farmer with no pending request needs the picker.
  const isIndependent =
    coop != null && coop.principalType === 'individual' && coop.pendingRequest === null;

  const joinableQuery = useQuery({
    queryKey: ['joinable-cooperatives'],
    queryFn: ({ signal }) => meControllerListJoinableCooperatives(signal),
    enabled: !!sub && isIndependent,
  });

  const joinMutation = useMutation({
    mutationFn: (cooperativeId: string) =>
      meControllerJoinCooperative({ cooperativeId }),
    onSuccess: () => {
      setJoinTarget(null);
      // Flip the screen to the pending state.
      queryClient.invalidateQueries({ queryKey: ['me-cooperative'] });
    },
  });

  const leaveMutation = useMutation({
    mutationFn: () => meControllerLeaveCooperative(),
    onSuccess: () => {
      setLeaveOpen(false);
      queryClient.invalidateQueries({ queryKey: ['me-cooperative'] });
      // The home-screen membership chip shares this key.
      queryClient.invalidateQueries({ queryKey: ['me-farm-profile'] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => meControllerCancelCooperativeRequest(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me-cooperative'] });
    },
  });

  // Could not derive the caller's identity from the token — surface as an error
  // rather than firing a query we know will be rejected.
  if (!sub) {
    return (
      <Stack spacing={3}>
        <Header t={t} />
        <Alert severity="error">{t('coop.noIdentity')}</Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Header t={t} />

      {coopQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : coopQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => coopQuery.refetch()}>
              {t('coop.retry')}
            </Button>
          }
        >
          {t('coop.loadError', { error: errMessage(coopQuery.error) })}
        </Alert>
      ) : isGroupMember ? (
        // ---- In an informal group, NOT a cooperative ----------------------
        // No coop "Leave" CTA here (it would wipe their group). A neutral note
        // points them at their group surface instead. Reuses existing copy
        // (no new i18n keys per TD-057 partition split).
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Text variant="title-small" color="text.primary">
                  {t('coop.member.label')}
                </Text>
                <Chip
                  kind="assist"
                  color="neutral"
                  leadingIcon="groups"
                  label={t('farmerDetail.principal.informalGroup')}
                />
              </Box>

              <Text variant="headline-small">
                {t('farmerDetail.principal.informalGroup')}
              </Text>

              <Text variant="body-medium" color="text.secondary">
                {t('groups.create.body')}
              </Text>

              <Box>
                <Button variant="outlined" onClick={() => navigate({ to: '/groups' })}>
                  {t('nav.groups')}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ) : isMember ? (
        // ---- Member: status + leave ---------------------------------------
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Text variant="title-small" color="text.primary">
                  {t('coop.member.label')}
                </Text>
                <Chip kind="assist" color="success" leadingIcon="groups" label={t('coop.member.chip')} />
              </Box>

              <Text variant="headline-small">
                {t('coop.status.member', { name: coop.cooperativeName ?? t('coop.unnamed') })}
              </Text>

              <Text variant="body-medium" color="text.secondary">
                {t('coop.member.explainer')}
              </Text>

              <Box>
                <Button
                  variant="outlined"
                  onClick={() => setLeaveOpen(true)}
                  disabled={leaveMutation.isPending}
                >
                  {t('coop.member.leave')}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ) : hasPending ? (
        // ---- Pending request: state + cancel ------------------------------
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Text variant="title-small" color="text.primary">
                  {t('coop.pending.label')}
                </Text>
                <Chip kind="assist" color="warning" leadingIcon="hourglass_empty" label={t('coop.pending.chip')} />
              </Box>

              <Text variant="headline-small">
                {t('coop.status.pending', {
                  name: coop.pendingRequest?.cooperativeName ?? t('coop.unnamed'),
                })}
              </Text>

              {coop.pendingRequest ? (
                <Text variant="body-medium" color="text.secondary">
                  {t('coop.pending.requestedAt', {
                    when: formatRequestedAt(coop.pendingRequest.requestedAt),
                  })}
                </Text>
              ) : null}

              <Text variant="body-medium" color="text.secondary">
                {t('coop.pending.explainer')}
              </Text>

              {cancelMutation.isError ? (
                <Alert severity="error">
                  {t('coop.pending.cancelError', { error: errMessage(cancelMutation.error) })}
                </Alert>
              ) : null}

              <Box>
                <Button
                  variant="outlined"
                  onClick={() => cancelMutation.mutate()}
                  disabled={cancelMutation.isPending}
                  startIcon={cancelMutation.isPending ? <CircularProgress size={16} /> : undefined}
                >
                  {t('coop.pending.cancel')}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        // ---- Independent, no pending: intro + joinable picker -------------
        <Stack spacing={2}>
          <Alert severity="info">{t('coop.independent.intro')}</Alert>

          {joinableQuery.isPending ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : joinableQuery.isError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => joinableQuery.refetch()}>
                  {t('coop.retry')}
                </Button>
              }
            >
              {t('coop.join.loadError', { error: errMessage(joinableQuery.error) })}
            </Alert>
          ) : (joinableQuery.data?.length ?? 0) === 0 ? (
            <Card>
              <CardContent>
                <Stack spacing={1}>
                  <Text variant="title-medium" as="h3">
                    {t('coop.join.empty.title')}
                  </Text>
                  <Text variant="body-medium" color="text.secondary">
                    {t('coop.join.empty.body')}
                  </Text>
                </Stack>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                {joinableQuery.data?.map((c, idx) => (
                  <ListItem
                    key={c.id}
                    leadingIcon="groups"
                    title={c.name}
                    noDivider={idx === (joinableQuery.data?.length ?? 0) - 1}
                    trailing={
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => setJoinTarget(c)}
                        disabled={joinMutation.isPending}
                      >
                        {t('coop.join.button')}
                      </Button>
                    }
                  />
                ))}
              </CardContent>
            </Card>
          )}

          {joinMutation.isError ? (
            <Alert severity="error">
              {t('coop.join.error', { error: errMessage(joinMutation.error) })}
            </Alert>
          ) : null}
        </Stack>
      )}

      {/* Leave confirmation */}
      <Dialog
        open={leaveOpen}
        onClose={() => {
          if (!leaveMutation.isPending) setLeaveOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('coop.member.leaveConfirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('coop.member.leaveConfirm.body', {
              name: coop?.cooperativeName ?? t('coop.unnamed'),
            })}
          </DialogContentText>
          {leaveMutation.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t('coop.member.leaveConfirm.error', { error: errMessage(leaveMutation.error) })}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => setLeaveOpen(false)}
            disabled={leaveMutation.isPending}
          >
            {t('coop.member.leaveConfirm.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => leaveMutation.mutate()}
            disabled={leaveMutation.isPending}
            startIcon={leaveMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {t('coop.member.leaveConfirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Join confirmation */}
      <Dialog
        open={joinTarget !== null}
        onClose={() => {
          if (!joinMutation.isPending) setJoinTarget(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('coop.join.confirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('coop.join.confirm.body', { name: joinTarget?.name ?? '' })}
          </DialogContentText>
          {joinMutation.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t('coop.join.error', { error: errMessage(joinMutation.error) })}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => setJoinTarget(null)}
            disabled={joinMutation.isPending}
          >
            {t('coop.join.confirm.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (joinTarget) joinMutation.mutate(joinTarget.id);
            }}
            disabled={joinMutation.isPending}
            startIcon={joinMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {t('coop.join.confirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
